import type { ApiResponse } from '../../api/contract';

export type CommissionLedgerEntry = ApiResponse<'/api/v1/commission-ledger/{id}', 'get'>['data'];
export type CommissionLedgerEntryDetail = CommissionLedgerEntry;
export type CommissionLedgerBeneficiaryType = CommissionLedgerEntry['beneficiaryType'];
export type CommissionLedgerEntryType = CommissionLedgerEntry['entryType'];
export type CommissionLedgerSort = 'effectiveAt' | 'createdAt';

export interface CommissionLedgerFilters {
  readonly partnerId: string;
  readonly agentId: string;
  readonly beneficiaryType: CommissionLedgerBeneficiaryType | '';
  readonly beneficiaryId: string;
  readonly beneficiaryUsername: string;
  readonly transactionId: string;
  readonly partnerTransactionId: string;
  readonly entryType: CommissionLedgerEntryType | '';
  readonly currency: string;
  readonly minimumAmount: string;
  readonly maximumAmount: string;
  readonly effectiveFrom: string;
  readonly effectiveTo: string;
}

export interface CommissionLedgerListQuery {
  readonly cursor: string;
  readonly pageSize: number;
  readonly sort: CommissionLedgerSort;
  readonly filters: CommissionLedgerFilters;
}

export interface CommissionAdjustmentInput {
  readonly beneficiaryId: string;
  readonly signedAmount: string;
  readonly currencyCode: string;
  readonly reason: string;
  readonly evidenceReference: string;
}

export interface CommissionAdjustmentPreview {
  readonly beneficiaryId: string;
  readonly signedAmount: string;
  readonly currencyCode: string;
  readonly availableBefore: string;
  readonly availableAfter: string;
  readonly reason: string;
  readonly evidenceReference: string;
}

export interface MockApprovalRequest {
  readonly approvalId: string;
  readonly status: 'PENDING_MOCK_APPROVAL';
  readonly submittedAt: string;
}

export function bangkokWallTimeToRfc3339(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) return '';
  if (/(?:Z|[+-]\d{2}:\d{2})$/u.test(trimmed)) return trimmed;
  const match = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?::(\d{2})(\.\d{1,6})?)?$/u.exec(trimmed);
  if (match === null) throw new Error('Expected a Bangkok wall time');
  return `${match[1]}:${match[2] ?? '00'}${match[3] ?? ''}+07:00`;
}

export function formatExactSignedMoney(amount: string, currency: string): string {
  const normalizedCurrency = currency.trim().toUpperCase();
  const negative = amount.startsWith('-');
  const magnitude = negative ? amount.slice(1) : amount;
  return `${negative ? '-' : ''}${normalizedCurrency}\u00a0${magnitude}`;
}

export function addExactSignedDecimals(left: string, right: string, minimumScale = 0): string {
  const leftParts = decimalParts(left);
  const rightParts = decimalParts(right);
  const scale = Math.max(leftParts.scale, rightParts.scale, minimumScale);
  const sum = scaledUnits(leftParts, scale) + scaledUnits(rightParts, scale);
  return formatScaledUnits(sum, scale);
}

export function compareExactSignedDecimals(left: string, right: string): number {
  const leftParts = decimalParts(left);
  const rightParts = decimalParts(right);
  const scale = Math.max(leftParts.scale, rightParts.scale);
  const leftUnits = scaledUnits(leftParts, scale);
  const rightUnits = scaledUnits(rightParts, scale);
  return leftUnits < rightUnits ? -1 : leftUnits > rightUnits ? 1 : 0;
}

interface DecimalParts {
  readonly negative: boolean;
  readonly units: bigint;
  readonly scale: number;
}

function decimalParts(value: string): DecimalParts {
  const match = /^(-)?(0|[1-9][0-9]*)(?:\.([0-9]+))?$/u.exec(value.trim());
  if (match === null) throw new Error('Expected a canonical signed decimal string');
  const whole = match[2];
  if (whole === undefined) throw new Error('Expected a decimal whole number');
  const fraction = match[3] ?? '';
  return {
    negative: match[1] === '-',
    units: BigInt(`${whole}${fraction}`),
    scale: fraction.length,
  };
}

function scaledUnits(parts: DecimalParts, scale: number): bigint {
  const magnitude = parts.units * 10n ** BigInt(scale - parts.scale);
  return parts.negative ? -magnitude : magnitude;
}

function formatScaledUnits(units: bigint, scale: number): string {
  const negative = units < 0n;
  const magnitude = negative ? -units : units;
  if (scale === 0) return `${negative ? '-' : ''}${magnitude}`;
  const factor = 10n ** BigInt(scale);
  const whole = magnitude / factor;
  const fraction = String(magnitude % factor).padStart(scale, '0');
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}
